import CarouselDirectionNav from 'components/sections/carousel/CarouselDirectionNav';
import CarouselIndexNav from 'components/sections/carousel/CarouselIndexNav';
import useCarousel, {
  OnCarouselIndexChangeAttemptProps,
} from 'components/sections/carousel/useCarousel';
import React, { Children, useRef, useEffect, useState } from 'react';
import { scrollTo } from 'utils/generalUtils';
import cx from 'utils/classnames';
import useCSSsettings from 'hooks/custom/forms/useCSSsettings';
import useOnScreen from 'hooks/custom/useOnScreen';
import CarouselSlide from './CarouselSlide';

interface CarouselProps {
  children: React.ReactNode;
  onSlideAnimationStart?: (index: number) => void;
  onSlideAnimationComplete?: (index: number) => void;
  currentIndex?: number;
  showNav?: boolean;
  variant?: 'coverFlow' | 'default';
  scrollTarget?: React.RefObject<HTMLElement>;
  scrollToActiveSlide?: boolean;
  stepSize?: number;
  onCarouselIndexChangeAttempt: (
    args: OnCarouselIndexChangeAttemptProps
  ) => void;
}

function Carousel({
  children,
  onSlideAnimationStart = (): void => {},
  onSlideAnimationComplete = (): void => {},
  currentIndex: controlledIndex,
  showNav = true,
  variant = 'default',
  scrollTarget,
  scrollToActiveSlide = true,
  stepSize = 1,
  onCarouselIndexChangeAttempt,
}: CarouselProps): React.ReactElement {
  const slides = Children.toArray(children).filter(React.isValidElement);
  const { currentIndex, gotoSlide, gotoNextSlide, gotoPrevSlide } = useCarousel(
    slides.length,
    onCarouselIndexChangeAttempt,
    controlledIndex,
    stepSize
  );
  const { headerHeight } = useCSSsettings();
  const lastIndexRef = useRef(currentIndex);

  const carouselRef = useRef<HTMLDivElement>(null);
  const slidesListRef = useRef<HTMLOListElement>(null);
  const [carouselWidth, setCarouselWidth] = useState(0);
  const [translateX, setTranslateX] = useState(0);
  const [transitionState, setTransitionState] = useState({
    isTransitioning: false,
    type: null as 'start' | 'end' | null,
  });
  const [isDraggingState, setIsDraggingState] = useState(false);
  const { isOnScreen } = useOnScreen(carouselRef, '-100px', {
    isOnScreen: true,
  });

  // Add touch event state
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const isDragging = useRef(false);
  const dragOffset = useRef(0);
  const baseTranslateX = useRef(0);

  const calculateTranslateX = (): void => {
    if (!carouselRef.current || !slidesListRef.current) return;

    const slidesList = slidesListRef.current;
    const slideElements = Array.from(slidesList.children);
    if (slideElements.length === 0) return;

    const firstSlide = slideElements[0] as HTMLElement;
    const slideWidth = firstSlide.offsetWidth;
    const gap = parseInt(getComputedStyle(slidesList).gap || '0', 10);
    // if the slideList container has padding on it, we need to offset the translation at the end of the listby the padding
    const paddingOffset =
      currentIndex === slideElements.length - 1
        ? parseInt(getComputedStyle(slidesList).padding || '0', 10)
        : 0;
    // Calculate translation based on first slide's position
    let newTranslateX = -(currentIndex * (slideWidth + gap)) - paddingOffset;

    // Prevent overflow at the end
    const lastSlide = slideElements[slideElements.length - 1] as HTMLElement;
    const containerWidth = carouselRef.current.offsetWidth;
    const maxTranslate = -(
      lastSlide.offsetLeft +
      lastSlide.offsetWidth -
      containerWidth +
      paddingOffset
    );

    if (newTranslateX < maxTranslate) {
      newTranslateX = maxTranslate;
    }

    // Prevent overflow at the start
    if (newTranslateX > 0) {
      newTranslateX = 0;
    }

    baseTranslateX.current = newTranslateX;
    setTranslateX(newTranslateX);
  };

  useEffect(() => {
    calculateTranslateX();

    const resizeObserver = new ResizeObserver(calculateTranslateX);
    if (carouselRef.current) {
      resizeObserver.observe(carouselRef.current);
    }

    return (): void => {
      resizeObserver.disconnect();
    };
  }, [currentIndex, slides.length]);

  useEffect(() => {
    const slidesList = slidesListRef.current;
    if (!slidesList) return;

    const handleTransitionStart = (e: TransitionEvent): void => {
      if (e.propertyName === 'transform' && e.target === slidesList) {
        setTransitionState({ isTransitioning: true, type: 'start' });
        onSlideAnimationStart(currentIndex);
      }
    };

    const handleTransitionEnd = (e: TransitionEvent): void => {
      if (e.propertyName === 'transform' && e.target === slidesList) {
        setTransitionState({ isTransitioning: false, type: 'end' });
        onSlideAnimationComplete(currentIndex);
      }
    };

    slidesList.addEventListener('transitionstart', handleTransitionStart);
    slidesList.addEventListener('transitionend', handleTransitionEnd);

    // eslint-disable-next-line consistent-return
    return (): void => {
      slidesList.removeEventListener('transitionstart', handleTransitionStart);
      slidesList.removeEventListener('transitionend', handleTransitionEnd);
    };
  }, [currentIndex, onSlideAnimationStart, onSlideAnimationComplete]);

  // set the carousel width
  useEffect(() => {
    if (carouselRef.current) {
      setCarouselWidth(carouselRef.current.offsetWidth);
    }
  }, [carouselRef]);

  // scroll to the active slide when index changes (controlled or uncontrolled)
  useEffect(() => {
    // Only scroll if the index actually changed
    if (currentIndex !== lastIndexRef.current && scrollToActiveSlide) {
      const targetElement = scrollTarget?.current || carouselRef.current;
      if (!targetElement) return;

      const headerOffset = headerHeight; // adjust this value based on your header height
      const elementPosition = targetElement.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - headerOffset;

      scrollTo(offsetPosition);
      lastIndexRef.current = currentIndex;
    }
  }, [currentIndex, scrollTarget, scrollToActiveSlide]);

  const handleGotoNext = (): void => {
    gotoNextSlide();
  };

  const handleGotoPrev = (): void => {
    gotoPrevSlide();
  };

  const handleGotoSlide = (index: number): void => {
    gotoSlide(index);
  };

  function getSlidePosition(index: number): 'prev' | 'next' | 'inactive' {
    if (index === currentIndex - 1) {
      return 'prev';
    }

    if (index === currentIndex + 1) {
      return 'next';
    }
    return 'inactive';
  }

  // Add touch event handlers
  const handleTouchStart = (e: TouchEvent): void => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    isDragging.current = true;
    setIsDraggingState(true);
    dragOffset.current = 0;

    // Store the current base translation when starting to drag
    baseTranslateX.current = translateX;

    // Temporarily disable CSS transitions during dragging
    if (slidesListRef.current) {
      slidesListRef.current.style.transition = 'none';
    }
  };

  const handleTouchMove = (e: TouchEvent): void => {
    if (!isDragging.current) return;

    const touchX = e.touches[0].clientX;
    const touchY = e.touches[0].clientY;
    const deltaX = touchX - touchStartX.current;
    const deltaY = touchY - touchStartY.current;

    // Only consider horizontal swipes
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      e.preventDefault();

      // Add slight resistance for more natural feel
      const resistance = 0.8;
      let resistedDeltaX = deltaX * resistance;

      // Add extra resistance at edges to prevent over-scrolling
      if (
        (currentIndex === 0 && resistedDeltaX > 0) ||
        (currentIndex === slides.length - 1 && resistedDeltaX < 0)
      ) {
        resistedDeltaX *= 0.3; // Extra resistance at edges
      }

      dragOffset.current = resistedDeltaX;

      // Apply real-time transform during dragging
      const realTimeTranslateX = baseTranslateX.current + resistedDeltaX;
      setTranslateX(realTimeTranslateX);
    }
  };

  const handleTouchEnd = (): void => {
    if (!isDragging.current) return;

    // Re-enable CSS transitions
    if (slidesListRef.current) {
      slidesListRef.current.style.transition = '';
    }

    const SWIPE_THRESHOLD = 50; // Minimum distance to trigger a slide change
    const SWIPE_VELOCITY_THRESHOLD = 0.3; // Minimum velocity to trigger a slide change

    // Calculate swipe velocity
    const velocity = Math.abs(dragOffset.current) / carouselWidth;

    if (
      Math.abs(dragOffset.current) > SWIPE_THRESHOLD ||
      velocity > SWIPE_VELOCITY_THRESHOLD
    ) {
      if (dragOffset.current > 0) {
        gotoPrevSlide();
      } else {
        gotoNextSlide();
      }
    } else {
      // If the swipe wasn't strong enough, animate back to the original position
      setTranslateX(baseTranslateX.current);
    }

    // Reset state
    isDragging.current = false;
    setIsDraggingState(false);
    dragOffset.current = 0;
  };

  useEffect(() => {
    const carouselElement = carouselRef.current;
    if (!carouselElement) return undefined;

    carouselElement.addEventListener('touchstart', handleTouchStart, {
      passive: false,
    });
    carouselElement.addEventListener('touchmove', handleTouchMove, {
      passive: false,
    });
    carouselElement.addEventListener('touchend', handleTouchEnd);
    carouselElement.addEventListener('touchcancel', handleTouchEnd);

    return (): void => {
      carouselElement.removeEventListener('touchstart', handleTouchStart);
      carouselElement.removeEventListener('touchmove', handleTouchMove);
      carouselElement.removeEventListener('touchend', handleTouchEnd);
      carouselElement.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [translateX, gotoNextSlide, gotoPrevSlide, isDraggingState]);

  return (
    <div
      data-testid="carousel"
      className={cx('carousel', {
        'carousel--isTransitioning': transitionState.isTransitioning,
        'carousel--transitionStart': transitionState.type === 'start',
        'carousel--transitionEnd': transitionState.type === 'end',
        'carousel--isDragging': isDraggingState,
        'carousel--isFirstSlide': currentIndex === 0,
        'carousel--isLastSlide': currentIndex === slides.length - 1,
        'carousel--isOnScreen': isOnScreen,
        [`carousel--${variant}`]: variant,
      })}
      style={
        {
          '--carouselSlideCount': slides.length,
          '--carouselWidth': `${carouselWidth}`,
          '--activeSlideIndex': currentIndex,
          '--translateX': `${translateX}px`,
        } as React.CSSProperties
      }
    >
      <div className="carousel__inner">
        <div className="carousel__slides" ref={carouselRef}>
          <ol className="carousel__slidesList" ref={slidesListRef}>
            {Children.map(slides, (child, index) => {
              const position = getSlidePosition(index);
              let relativePath: 'beforeActive' | 'afterActive' | undefined;
              const relativeIndex = index - currentIndex;

              if (index !== currentIndex) {
                if (index < currentIndex) {
                  relativePath = 'beforeActive';
                } else if (index > currentIndex) {
                  relativePath = 'afterActive';
                }
              }

              return (
                <CarouselSlide
                  isActive={index === currentIndex}
                  position={position}
                  relativePath={relativePath}
                  relativeIndex={relativeIndex}
                >
                  {child}
                </CarouselSlide>
              );
            })}
          </ol>
        </div>
        {slides.length > 1 && showNav && (
          <div className="carousel__nav">
            <CarouselIndexNav
              currentIndex={currentIndex}
              slides={slides as React.ReactElement[]}
              handleGotoSlide={handleGotoSlide}
            />
            <CarouselDirectionNav
              currentIndex={currentIndex}
              totalSlides={slides.length}
              handleGotoNext={handleGotoNext}
              handleGotoPrev={handleGotoPrev}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default Carousel;
